import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
    MatLegacyDialog as MatDialog,
    MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { IAppRequest, InterfaceSelectList } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { CustomValidators } from '@app/app.validator';
import { AppHelper } from '@app/core/app.helper';
import {
    DEFAULT_CROP_OPTIONS,
    DEFAULT_CROPPER_DIALOG_DATA,
    ICropperDialogData,
} from '@app/dialogs-helper/cropper-dialog/cropper-dialog.interface';
import { CatalogDialogComponent } from '@app/modules/catalogs/dialogs/catalog-dialog/catalog-dialog.component';
import { IImagePickerActions, IImagePickerData } from '@components/image-picker/image-picker.interface';
import { LANG_DICTIONARY_TOKEN, SERVER_URL_TOKEN } from '@constants/diConstant';
import { InterfaceAccessMatrixUnit } from '@modules/login/interfaces/login.interface';
import { IUserListDialogData } from '@modules/users/interfaces/user-list.interface';
import { NotifierService } from '@services/notifier.service';
import { TuiDestroyService } from '@taiga-ui/cdk';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { UserListService } from '../../../services/user-list.service';

@Component({
    selector: 'app-user-list-dialog',
    templateUrl: './user-list-dialog.component.html',
    styleUrls: ['../styles/user-list-dialog.component.css'],
    providers: [TuiDestroyService],
})
export class UserListDialogComponent implements OnInit {
    readonly #destroy$ = inject(TuiDestroyService);
    private readonly dialog = inject(MatDialog);
    private readonly spinner = inject(NgxSpinnerService);
    private readonly notifier = inject(NotifierService);
    private readonly dialogRef = inject(MatDialogRef<UserListDialogComponent>);
    private readonly formBuilder = inject(FormBuilder);
    private readonly service = inject(UserListService);
    readonly data: IUserListDialogData = inject(MAT_DIALOG_DATA);
    readonly appService = inject(AppService);
    readonly dictionary = inject(LANG_DICTIONARY_TOKEN);
    readonly serverUrl = inject(SERVER_URL_TOKEN);

    readonly now = new Date();

    /**
     * Получить список пунктов меню
     */
    getMenuListForUser: Observable<InterfaceSelectList>;

    photoIds = [];

    /**
     * Форма
     */
    form: FormGroup = null;

    /**
     * Ссылка на форму
     */
    @ViewChild('formRef', { static: false }) formRef: ElementRef;

    /**
     * Флаг загрузки формы
     */
    isLoaded = false;

    /*
     * Настроки валидации
     */
    validationPass = null;

    /**
     * Доступ по рубрикаторам
     */
    accessCatalogs: InterfaceAccessMatrixUnit = null;
    @ViewChild('positionRef', { static: false }) positionRef;

    /**
     * Выбранное фото
     */
    photoUrl: string = null;

    /**
     * Настройки диалога редактирования фото
     */
    cropperOptions: ICropperDialogData = {
        ...DEFAULT_CROPPER_DIALOG_DATA,
        cropOptions: {
            ...DEFAULT_CROP_OPTIONS,
            title: this.dictionary.files_crop_first_step_title,
            description: this.dictionary.files_crop_first_step_description,
            maintainAspectRatio: false,
            resizeToWidth: 600,
            resizeToHeight: 600,
        },
        avatarOptions: {
            title: this.dictionary.files_crop_two_step_title,
            description: this.dictionary.files_crop_two_step_description,
            resizeToWidth: 64,
            resizeToHeight: 64,
        },
        useAvatarStep: true,
        data: '',
    };

    /**
     * Действия для загрузки и удаления фото
     */
    readonly photoActions: IImagePickerActions = {
        upload: (data: IImagePickerData) =>
            this.service.savePhoto({
                photo: {
                    field: 'photo',
                    value: {
                        name: data.name,
                        file: data.box.split('base64,')[1],
                    },
                },
                photo_round: {
                    value: {
                        name: data.name,
                        file: data.round.split('base64,')[1],
                    },
                },
            }),
        remove: () => this.service.removePhoto({ ids: this.photoIds }),
    };

    userList = [];

    /**
     *
     * @param dialogRef -ссылка на диалог
     * @param formBuilder -создатель форм
     * @param service -сервисы
     * @param appService -сервисы
     * @param spinner -прелоадер
     * @param notifier -уведомления
     * @param dialog -диалог
     * @param data -данные для диалогового окна
     */
    constructor() {
        this.validationPass = this.appService.getSettingsByKey('password_validation');
    }

    /** Событие окончания инициализации компонента */
    ngOnInit() {
        this.createForm(this.data.data, this.data.id);
        this.accessCatalogs = this.appService?.accessMatrix?.rubricator;
        if (this.data.id) {
            this.getMenuListForUser = this.appService.getRegisters([`menu_for_user(${this.data.id})`]).pipe(
                filter((result) => !result.is_error),
                map((result) => result.data[`menu_for_user(${this.data.id})`]),
            );
        }
    }

    /**
     * Создание формы
     * @param data - данные формы
     * @param id - id пользователя
     */
    createForm(data?, id?) {
        const passwordValidators = [
            CustomValidators.patternValidator(
                /^[0-9A-Za-z\!\@\#\$\%\^\&\*\(\)\_\+\-\=\{\}\;\'\:\"\|\,\.\<\>\/\?\[\]\№\~\`\\]+$/,
                { hasLatinCharacters: true },
            ),
            CustomValidators.patternValidator(/\d/, { hasNumber: true }),
            CustomValidators.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
            CustomValidators.patternValidator(/[a-z]/, { hasSmallCase: true }),
            CustomValidators.patternValidator(/[\!\@\#\$\%\^\&\*\(\)\_\+\-\=\{\}\;\'\:\"\|\,\.\<\>\/\?\[\]\№\~\`\\]/, {
                hasSpecialCharacters: true,
            }),
            Validators.minLength(8),
        ];
        this.form = this.formBuilder.group({
            firstname: [data.firstname ? data.firstname : '', [Validators.required]],
            lastname: [data.lastname ? data.lastname : '', [Validators.required]],
            middlename: [data.middlename ? data.middlename : '', []],
            roles: id ? [data.role_id, []] : ['', [Validators.required]],
            subject_id: id ? [data.subject_id, []] : ['', [Validators.required]],
            snils: [data.snils ? data.snils : '', []],
            login: [data.login ? data.login : '', [Validators.required]],
            passwordGroup: this.formBuilder.group(
                {
                    password: id ? ['', passwordValidators] : ['', [Validators.required, ...passwordValidators]],
                    repeat_password: id ? [] : ['', [Validators.required]],
                },
                { validator: CustomValidators.childrenEqual },
            ),
            chief: [data.chief ? data.chief : '', []],
            contacts: [data.contacts ? data.contacts : '', []],
            email: [data.email ? data.email : '', [Validators.email]],
            position: [data.position, [Validators.required]],
            company: [data.company ? data.company : '', []],
            phone: [data.phone ? data.phone : '', []],
            fax: [data.fax ? data.fax : '', []],
            address: [data.address ? data.address : '', []],
            comments: [data.comments ? data.comments : '', []],
            ip: [data.ip ? data.ip : '', []],
            active: [data.active ?? true],
            access_child: [data.access_child, []],
            date_start_deactivation: [data.date_start_deactivation ? data.date_start_deactivation : null, []],
            date_end_deactivation: [data.date_end_deactivation ? data.date_end_deactivation : null, []],
            date_birth: [data.date_birth ? data.date_birth : null, []],
            personal_email: [data.personal_email ? data.personal_email : null, []],
            personal_phone: [data.personal_phone ? data.personal_phone : null, []],
            menu_id: [data.menu_id ?? null, []],
        });
        if (this.validationPass === '0') {
            this.form.get('passwordGroup').get('password').clearValidators();
            this.form.get('passwordGroup').get('password').setValidators(Validators.minLength(3));
        }
        this.form
            .get('active')
            .valueChanges.pipe(takeUntil(this.#destroy$))
            .subscribe(() => {
                this.form.get('date_start_deactivation').setValue(null);
                this.form.get('date_end_deactivation').setValue(null);
            });
        if (id > 0) {
            this.checkUserHead(id);
        }

        if (data && data.gallery && data.gallery.data) {
            this.photoIds = data.gallery.ids;
            this.photoUrl = this.serverUrl + '/public/uploads/user_photos/' + data.gallery.data?.photo?.file_name;
        }
        this.isLoaded = true;
    }

    /**
     * Закрытие формы
     */
    close() {
        this.dialogRef.close({ save: false });
    }

    /**
     * Сохранение данных
     */
    save() {
        if (this.form.valid) {
            const data = Object.assign({}, this.form.getRawValue());
            data.gallery = this.photoIds;
            data.id = this.data.id ? this.data.id : 0;
            if (this.form.value.passwordGroup.password) {
                data.password = this.form.value.passwordGroup.password;
            }
            data.region_id = '0';
            data.chief = data.chief ? data.chief : 0;
            data.active = data.active === true ? '1' : '0';
            data.access_child = data.access_child === true ? '1' : '0';
            this.spinner.show();
            if (this.data.isEditYourself) {
                this.service
                    .saveYourself(data)
                    .toPromise()
                    .then((result: any) => {
                        this.spinner.hide();
                        if (result && result.is_error === false) {
                            this.dialogRef.close({ save: true, data: result?.data });
                        } else {
                            AppHelper.setErrorForm(result, this.form, this.formRef, this.notifier);
                        }
                    })
                    .catch(() => {
                        this.spinner.hide();
                        this.notifier.notify(
                            'error',
                            this.appService.language_dictionary.system_no_connection_with_system,
                        );
                    });
            } else {
                this.service
                    .save(data)
                    .toPromise()
                    .then((result: any) => {
                        this.spinner.hide();
                        if (result && result.is_error === false) {
                            this.dialogRef.close({ save: true, data: result?.data });
                        } else {
                            AppHelper.setErrorForm(result, this.form, this.formRef, this.notifier);
                        }
                    })
                    .catch(() => {
                        this.spinner.hide();
                        this.notifier.notify(
                            'error',
                            this.appService.language_dictionary.system_no_connection_with_system,
                        );
                    });
            }
        } else {
            AppHelper.validateAllFormFields(this.form);
            setTimeout(() => {
                const target = this.formRef.nativeElement.querySelector(
                    '.mat-select.ng-invalid, .mat-input-element.ng-invalid, .mat-checkbox-input.ng-invalid',
                );
                AppHelper.focusInput(target);
            }, 0);
        }
    }

    /**
     * Добавление в рубрикаторы
     */
    addResult() {
        const parentRubricatorId = this.appService.getSettingsByKey('directory_of_posts');
        const dialogRef = this.dialog.open(CatalogDialogComponent, {
            disableClose: true,
            autoFocus: false,
            width: '600px',
            data: {
                title: this.appService.language_dictionary.rubric_adding_data_for_rubric,
                id: 0,
                access: this.accessCatalogs,
                data: {
                    parent_id: parentRubricatorId,
                },
            },
        });
        dialogRef
            .keydownEvents()
            .pipe(takeUntil(this.#destroy$))
            .subscribe((result) => {
                if (result.code === 'Escape') {
                    dialogRef.close({ save: false });
                }
            });
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.#destroy$))
            .subscribe((result) => {
                if (result.save) {
                    this.spinner.show();
                    this.appService
                        .getRegisters(['directory_of_posts'])
                        .toPromise()
                        .then((request: IAppRequest) => {
                            this.spinner.hide();
                            if (request && request.is_error === false) {
                                // Установка значений в селект
                                this.positionRef.setOptions(request.data.directory_of_posts);
                                this.notifier.notify(
                                    'success',
                                    this.appService.language_dictionary.system_data_successfully_saved,
                                );
                            } else {
                                this.notifier.notify('error', request.message);
                            }
                        })
                        .catch(() => {
                            this.spinner.hide();
                            this.notifier.notify(
                                'error',
                                this.appService.language_dictionary.system_no_connection_with_system,
                            );
                        });
                }
            });
    }

    /**
     * Удаляет из списка пользователей самого пользователя
     * @param id текущего пользователя
     */
    checkUserHead(id: number) {
        this.userList = this.data.guides.users.filter((item) => {
            return Number(item.id) !== id;
        });
    }

    /**
     * ЕСИА
     */
    authByEsia() {
        document.location.href = this.appService.serverUrl + '/esia/connect';
    }

    /**
     * Проверка прав на редактирования
     */
    canEdit(): boolean {
        return (
            this.appService?.accessMatrix?.users?.edit === 1 ||
            (this.appService?.accessMatrix?.users?.edit_yourself === 1 &&
                Number(this?.data?.id) === Number(this.appService.userData.id))
        );
    }

    /**
     * Обработка загрузки файлов
     */
    fileUploaded(data: any) {
        this.photoIds = data.ids;
    }

    /**
     * Обработка ошибок загрузки и удаления файлов
     */
    fileFailed(err: unknown) {
        this.notifier.exception(err);
    }

    /**
     * Обработка удаления файлов
     */
    fileRemoved() {
        this.photoUrl = null;
        this.photoIds = [];
    }
}
